import { Box } from '@mui/material'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'

import 'swiper/css'
import 'swiper/css/navigation'

import { debounce } from '@/shared/lib'
import { Arrow } from '@/shared/ui/Arrow'
import { VodArrowLeft, VodArrowRight } from '@/shared/ui/Icons'
import {
    vodSliderButtonHeight,
    vodSliderButtonWidth,
} from '@/shared/constants/vodConstants'
import { debounceSpeedShort } from '@/shared/constants/appConstants'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { pageType } from '@/shared/store/types'
import VodItemView from '@/features/VOD/components/vodItem/VodItemView'
import { useVodItemsContext } from '@/features/VOD/context/UseGetVodItemContext/useVodItemsContext'

interface ISlider {
    buttonHeight?: number

    vodItemHeight: number
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
    scrollPosition: {
        x: number
        y: number
    }
    hideCaptions?: boolean
    hideProgress?: boolean
    rowHeightPercentage?: number
    cardRadiusDp?: number
    isHomeContentRow?: boolean
}

const VodRecommendationStreamIQSlider = ({
    buttonHeight,
    vodItemHeight,
    handleGoToCategoryButtonClick,
    scrollPosition,
    hideCaptions,
    hideProgress,
    rowHeightPercentage,
    cardRadiusDp,
}: ISlider) => {
    const [swipe, setSwipe] = useState<SwiperType>()
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)
    const activeSlideIndexRef = useRef(0)
    const { recommendationItems, getRecommendationItems } = useVodItemsContext()

    const handleSlideChange = () => {
        if (swipe) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
            activeSlideIndexRef.current = swipe.activeIndex
        }
    }
    const leftClick = debounce(() => {
        swipe?.slidePrev()
        handleSlideChange()
    }, debounceSpeedShort)

    const rightClick = debounce(() => {
        swipe?.slideNext()
        handleSlideChange()
    }, debounceSpeedShort)

    const LeftArrowWrapper = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: {
            xs: '15%',
            md: buttonHeight ? buttonHeight * 0.4 : vodSliderButtonWidth,
        },
        height: buttonHeight ? buttonHeight : vodSliderButtonHeight,
        opacity: isAtStart ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    const RightArrowWrapper = {
        position: 'absolute',
        right: 0,
        top: 0,
        width: {
            xs: '15%',
            md: buttonHeight ? buttonHeight * 0.4 : vodSliderButtonWidth,
        },
        height: buttonHeight ? buttonHeight : vodSliderButtonHeight,
        opacity: isAtEnd ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    const shadowBoxStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'black',
        borderRadius: '50%',
        opacity: 0.3,
        top: 0,
        left: 0,
        zIndex: 8,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    useEffect(() => {
        if (swipe) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
            swipe.slideTo(activeSlideIndexRef.current, 0)
        }
    }, [recommendationItems, swipe])

    useEffect(() => {
        swipe?.on('slideChange', handleSlideChange)

        getRecommendationItems()

        handleSlideChange()

        return () => {
            swipe?.off('slideChange', handleSlideChange)
        }
    }, [])

    return (
        <Box position="relative">
            <Swiper
                onBeforeInit={(swiper: SwiperType) => setSwipe(swiper)}
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={16}
                slidesPerGroup={2}
                slidesPerView="auto">
                {recommendationItems.map((item: VodItem, index) => (
                    <SwiperSlide
                        key={item.id || index}
                        style={{ width: 'min-content' }}>
                        <VodItemView
                            hideCaptions={hideCaptions}
                            hideProgress={hideProgress}
                            vodItemHeight={vodItemHeight}
                            cardRadiusDp={cardRadiusDp}
                            rowHeightPercentage={rowHeightPercentage}
                            item={item}
                            handleGoToCategoryButtonClick={
                                handleGoToCategoryButtonClick
                            }
                            scrollPosition={scrollPosition}
                            isHomeContentItem={false}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>

            <Box position="absolute" top="30%" left="0px" width="100%">
                <Box sx={LeftArrowWrapper} onClick={leftClick}>
                    <Arrow icon={<VodArrowLeft />} />
                    <Box sx={shadowBoxStyle} />
                </Box>

                <Box sx={RightArrowWrapper} onClick={rightClick}>
                    <Arrow icon={<VodArrowRight />} />
                    <Box sx={shadowBoxStyle} />
                </Box>
            </Box>
        </Box>
    )
}
export default VodRecommendationStreamIQSlider
